import * as Schema from "effect/Schema";
import { DISMISS_CATEGORIES, FindingPlanPayload } from "./findingPlanning.ts";
import { FINDING_STATUSES } from "./findingStatus.ts";

/** Policy and finding wire contracts shared by the domain API, workspace proxy and web client. */
export const PolicyFileVersion = Schema.Struct({
  version: Schema.Number,
  filename: Schema.String,
  uploadedAt: Schema.String,
  uploadedByEmail: Schema.NullOr(Schema.String),
  note: Schema.NullOr(Schema.String),
});
export type PolicyFileVersion = typeof PolicyFileVersion.Type;

/**
 * The template a document was drafted from, as it stood when it was drafted.
 * Lives here rather than beside the template contracts so the policy contract
 * stays the module everything else imports.
 */
export const PolicyTemplateOrigin = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  version: Schema.Number,
});
export type PolicyTemplateOrigin = typeof PolicyTemplateOrigin.Type;

export const Policy = Schema.Struct({
  id: Schema.String,
  filename: Schema.String,
  /** Display title from governance; null means "show the filename". */
  title: Schema.NullOr(Schema.String),
  /** 1 for the original upload; +1 for every replacement. */
  version: Schema.Number,
  /** What the uploader said about the current file, if anything. */
  versionNote: Schema.NullOr(Schema.String),
  /** When and by whom the current file arrived (v1: the policy's own upload). */
  versionUploadedAt: Schema.String,
  versionUploadedByEmail: Schema.NullOr(Schema.String),
  /** Superseded files, oldest first. Empty for a never-replaced policy. */
  fileVersions: Schema.Array(PolicyFileVersion),
  status: Schema.Literals(["processing", "ready", "failed"]),
  /** Which shelf it sits on: the library and procedures share one table. */
  documentKind: Schema.Literals(["policy", "procedure", "framework"]),
  /** The firm's own framework this document sits under, when organised. */
  governanceFrameworkId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  /**
   * Reference material: a regulator handbook, a guidance note, an internal
   * explainer — filed so eve can ground answers in it, not a document the
   * firm is accountable for. It carries no owner and no review cadence, and
   * "never reviewed" is not a finding against it.
   *
   * A flag rather than a fourth `documentKind`: the shelf a document sits on
   * and whether the firm is accountable for it are separate questions, and a
   * reference document still wants the versions, text and search every other
   * document has. Absent on every document filed before this existed, which
   * reads the same as false.
   */
  reference: Schema.optionalKey(Schema.Boolean),
  /** The template it was drafted from; absent for everything else. */
  template: Schema.optionalKey(PolicyTemplateOrigin),
  sourceType: Schema.String,
  jurisdictionSlugs: Schema.Array(Schema.String),
  /** How the jurisdictions were set: by a person, or detected from the text. */
  jurisdictionSource: Schema.NullOr(Schema.Literals(["chosen", "detected"])),
  createdAt: Schema.String,
  uploadedByEmail: Schema.NullOr(Schema.String),
  /**
   * Download path on *this* API, never the storage origin: bytes stay behind
   * the tenant guard instead of living on a public, unguessable blob URL.
   */
  downloadPath: Schema.String,
  /**
   * What ingestion recorded about the document. Null until the run that
   * produces each number finishes — a document page shows "—" rather than a
   * confident zero for work that has not happened yet.
   */
  /** Bytes of the current file. Absent on documents filed before it was recorded. */
  sizeBytes: Schema.optionalKey(Schema.Number),
  pageCount: Schema.NullOr(Schema.Number),
  chunkCount: Schema.NullOr(Schema.Number),
  embeddingTokens: Schema.NullOr(Schema.Number),
  processingMs: Schema.NullOr(Schema.Number),
});
export type Policy = typeof Policy.Type;

export const OrgJurisdiction = Schema.Struct({
  slug: Schema.String,
  name: Schema.String,
});

export const PolicyList = Schema.Struct({
  policies: Schema.Array(Policy),
  /** The org's adopted jurisdictions — the choices an upload may be tagged with. */
  jurisdictions: Schema.Array(OrgJurisdiction),
});
export type PolicyList = typeof PolicyList.Type;

/**
 * A file name a person supplies, as it will be stored and later echoed into a
 * download's `Content-Disposition`. Any Unicode is a legitimate name
 * (`données protection €2026.pdf`, CJK, emoji), but a C0 control character or
 * DEL never is: CR/LF could split a header and a NUL made the upload hang
 * instead of answering. Refused at the boundary so none is ever stored.
 */
// eslint-disable-next-line no-control-regex
const NO_CONTROL_CHARACTERS = /^[^\u0000-\u001f\u007f]*$/;
export const UploadFilename = Schema.String.check(
  Schema.isPattern(NO_CONTROL_CHARACTERS, {
    message: "File names cannot contain control characters.",
  }),
);

export const PolicyUpload = Schema.Struct({
  filename: UploadFilename.check(Schema.isMinLength(1), Schema.isMaxLength(300)),
  contentBase64: Schema.String.check(Schema.isMinLength(1)),
  jurisdictionSlugs: Schema.Array(Schema.String),
  /**
   * Which shelf the document lands on. Optional, and absent means `policy` —
   * the default every upload had before this existed, so the Policies page
   * and anything else posting this endpoint keep their behaviour.
   */
  documentKind: Schema.optional(Schema.Literals(["policy", "procedure"])),
  /**
   * Reference material rather than a document the firm is accountable for:
   * filed so eve can ground answers in it, with no governance expected of it.
   * See `Policy.reference`.
   */
  reference: Schema.optional(Schema.Boolean),
  /**
   * The template this document was drafted from. The API then refuses the
   * file while any blank is left in it, and records the template on the
   * policy. The template must be one the organisation can see.
   */
  templateId: Schema.optional(Schema.String),
});
export type PolicyUpload = typeof PolicyUpload.Type;

export const PolicyFileUpload = Schema.Struct({
  expectedVersion: Schema.optional(Schema.Int.check(Schema.isGreaterThan(0))),
  filename: UploadFilename,
  contentBase64: Schema.String,
  note: Schema.optional(Schema.NullOr(Schema.String)),
});
export type PolicyFileUpload = typeof PolicyFileUpload.Type;

export const DecisionProofPayload = Schema.Struct({
  filename: UploadFilename.check(Schema.isMinLength(1), Schema.isMaxLength(255)),
  contentBase64: Schema.String,
});
export type DecisionProofPayload = typeof DecisionProofPayload.Type;

export const DecisionProofView = Schema.NullOr(Schema.Struct({ filename: Schema.String }));

export const ProofDownload = Schema.Struct({
  filename: Schema.String,
  contentBase64: Schema.String,
});

export const PolicyFindingView = Schema.Struct({
  id: Schema.String,
  changeId: Schema.String,
  /** Exact creating Inspector thread when recorded; older findings omit it. */
  inspectorThreadId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  inspectorRunTitle: Schema.optionalKey(Schema.NullOr(Schema.String)),
  policyId: Schema.String,
  title: Schema.String,
  summary: Schema.String,
  jurisdictionSlug: Schema.String,
  sourceUrl: Schema.String,
  sourceTitle: Schema.NullOr(Schema.String),
  publishedAt: Schema.NullOr(Schema.String),
  detectedAt: Schema.String,
  severity: Schema.NullOr(Schema.String),
  assessment: Schema.NullOr(Schema.String),
  requiredChanges: Schema.Array(Schema.String),
  /**
   * Which revision of the document the assessment above was grounded in, and
   * which revision the document is on now — see `@repo/shared/findingStaleness`,
   * which is the only thing that should compare them. Optional keys because a
   * server that predates them answers without them, and the reader's verdict
   * for a missing pair is the same as for a null one: unknown, never stale.
   */
  assessedRevision: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  policyRevision: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  assessedVersion: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  currentVersion: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  source: Schema.Literals(["monitor", "manual", "agent"]),
  status: Schema.Literals(FINDING_STATUSES),
  resolvedVersion: Schema.NullOr(Schema.Number),
  resolvedAt: Schema.NullOr(Schema.String),
  resolvedByEmail: Schema.NullOr(Schema.String),
  resolutionNote: Schema.NullOr(Schema.String),
  decisionProof: DecisionProofView,
  /**
   * Phase-1 planning and dismissal detail. Optional keys: an older server
   * answers without them, which reads as unassigned, undated, uncategorised.
   */
  dismissCategory: Schema.optionalKey(Schema.NullOr(Schema.Literals(DISMISS_CATEGORIES))),
  assigneeId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  dueDate: Schema.optionalKey(Schema.NullOr(Schema.String)),
  createdAt: Schema.String,
});

export const PolicyFindingList = Schema.Struct({
  findings: Schema.Array(PolicyFindingView),
});

export const PolicyFindingTransitionPayload = Schema.Struct({
  status: Schema.Literals(FINDING_STATUSES),
  /** Required when resolving. */
  resolvedVersion: Schema.optional(Schema.NullOr(Schema.Int.check(Schema.isGreaterThan(0)))),
  note: Schema.optional(Schema.NullOr(Schema.String)),
  /** Set to "agent" when eve acts for the principal; recorded in the trail. */
  via: Schema.optional(Schema.Literals(["manual", "agent"])),
  /** Optional supporting file; a closure's reason is required regardless. */
  proof: Schema.optional(DecisionProofPayload),
  /** Why a closure dismisses it; ignored on any other status. */
  dismissCategory: Schema.optional(Schema.Literals(DISMISS_CATEGORIES)),
});
export type PolicyFindingTransitionPayload = typeof PolicyFindingTransitionPayload.Type;

/**
 * The finding endpoint's body: a decision, or a plan (assignee, due date) —
 * never both. A union member ignores keys it does not declare, so each one
 * refuses the other's key: a body with both would otherwise decode as a
 * decision and drop the plan without a word.
 */
export const PolicyFindingWritePayload = Schema.Union([
  Schema.Struct({ ...PolicyFindingTransitionPayload.fields, plan: Schema.optionalKey(Schema.Never) }),
  Schema.Struct({ ...FindingPlanPayload.fields, status: Schema.optionalKey(Schema.Never) }),
]);
export type PolicyFindingWritePayload = typeof PolicyFindingWritePayload.Type;

export const PolicyVersionView = Schema.Struct({
  id: Schema.String,
  version: Schema.Number,
  source: Schema.Literals(["ingestion", "amendment", "publish"]),
  note: Schema.NullOr(Schema.String),
  createdBy: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  /** Size of the frozen text, so the list can hint at substance without it. */
  contentLength: Schema.Number,
});

export const PolicyVersionList = Schema.Struct({
  versions: Schema.Array(PolicyVersionView),
});

export const PolicyVersionContent = Schema.Struct({
  version: Schema.Number,
  source: Schema.Literals(["ingestion", "amendment", "publish"]),
  note: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  contentMarkdown: Schema.String,
});
